import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useSearchAllPeople } from '../../../../api/people'
import useDebouncedValue from '../../../../hooks/useDebounceValue'
import Skeleton from '../../../atoms/Skeleton'
import RoundedRectangleNode from '../Canvas/nodes/RoundedRectangleNode'
import SydInput from '../../../commonDesign/SydInput'
import { ICON_NAMES } from '../../../../constants'
import SydLabel from '../../../commonDesign/SydLabel'
import { useAppContext } from '../../../../redux/slices/appContext'
import { useEntityGraphContext } from '../Wrapper/EntityGraphProvider'
import { getRandomId } from './utils'

const getPersonData = (person) => ({
  header: person.firstName + ' ' + person.lastName,
  description: person.personType?.longName,
  nodeProps: {
    size: 'sm'
  },
  entityProps: {
    levelTypeId: 200,
    levelId: person.personId,
    type: 'person'
  },
  displayOptions: {
    header: 'entityProps.label',
    description: 'entityProps.description',
    image: 'entityProps.image'
  }
})

export const PersonsList = ({
  actionType = 'creation',
  onSelectItem: _onSelectItem,
  selectedValue
}) => {
  const { clientId } = useAppContext()
  const { dispatch } = useEntityGraphContext()
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300)

  const addLinkedDataToContext = useCallback((person) => {
    dispatch({
      type: 'ADD_LINKED_ENTITY_DATA',
      payload: {
        levelTypeId: 200,
        levelId: person.personId,
        value: {
          label: `${person.firstName} ${person.lastName}`,
          description: person.personType?.longName,
          image: person.profilePic ?? undefined,
          ...person
        }
      }
    })
  }, [dispatch])

  const onSelectItem = useCallback((person) => {
    addLinkedDataToContext(person)
    _onSelectItem?.(getPersonData(person))
  }, [addLinkedDataToContext, _onSelectItem])

  const { data, isLoading } = useSearchAllPeople({
    filters: {
      assignedToClientIds: [clientId],
      ...(selectedValue && {
        personId: [{ op: 'eq', value: selectedValue.levelId }]
      })
    },
    includes: { personTypes: true },
    ...(debouncedSearchTerm?.length && {
      textSearch: {
        accountName: debouncedSearchTerm,
        accountNumber: debouncedSearchTerm
      }
    })
  })

  const onDragStart = (event, account) => {
    addLinkedDataToContext(account)
    event.dataTransfer.setData('application/reactflow', JSON.stringify({
      nodeType: 'roundedRectangle',
      id: `account_${account.accountId}_${getRandomId()}`,
      data: getPersonData(account)
    }))
    event.dataTransfer.effectAllowed = 'move'
  }

  if (selectedValue) {
    return (
      <>
        {isLoading && (
          <Skeleton width='100%' height='38px' />
        )}
        {data?.data?.map((person) => (
          <RoundedRectangleNode
            key={person.personId}
            width='100%'
            height='auto'
            data={{
              ...getPersonData(person),
              displayOptions: {},
              nodeProps: {
                size: 'md',
                handles: [],
                targets: []
              }
            }}
          />
        ))}
      </>
    )
  }

  return (
    <>
      <div>
        <SydInput
          size='sm'
          iconName={ICON_NAMES.search}
          placeholder='Search persons...'
          onChange={(e) => setSearchTerm(e.target.value)}
          value={searchTerm}
          className='mb-2'
        />
      </div>
      <div className='flex flex-col gap-sm'>

        <SydLabel label={actionType === 'linking' ? 'Click a person to link them' : 'Drag person into the chart'} />
        {isLoading && Array.from({ length: 10 }).map((_, index) => (
          <Skeleton width='100%' height='38px' key={index} />
        ))}
        {data?.data?.map((person) => (
          <div
            draggable={actionType === 'creation'}
            key={person.personId}
            className='relative'
            style={{
              cursor: actionType === 'creation' ? 'grab' : 'pointer'
            }}
            onDragStart={(e) => onDragStart(e, person)}
            onClick={() => {
              if (actionType === 'linking' && onSelectItem) {
                onSelectItem(person)
              }
            }}
          >
            <RoundedRectangleNode
              width='100%' height='auto' data={{
                ...getPersonData(person),
                displayOptions: {},
                nodeProps: {
                  size: 'md',
                  handles: [],
                  targets: []
                }
              }}
            />
          </div>
        ))}
      </div>
    </>
  )
}

PersonsList.propTypes = {
  actionType: PropTypes.oneOf(['creation', 'linking']),
  onSelectItem: PropTypes.func,
  selectedValue: PropTypes.shape({
    levelId: PropTypes.string,
    type: PropTypes.string
  })
}
